// Shares the login between open tabs of this app.
//
// The login stays in each tab's sessionStorage (so closing every tab still ends it, and nothing is
// kept on disk). A newly opened tab asks the other open tabs for it over a BroadcastChannel; any tab
// that is signed in answers. Token refreshes and logouts are passed on the same way, so all tabs keep
// working with the current tokens and sign out together.
//
// Browsers without BroadcastChannel simply keep the old behaviour (sign in per tab).

const CHANNEL_NAME = "feed-auth";
const SESSION_KEYS = ["accessToken", "refreshToken", "userData", "activeUserTypeCode"];
const ASK_TIMEOUT_MS = 400;

const channel = typeof BroadcastChannel !== "undefined" ? new BroadcastChannel(CHANNEL_NAME) : null;
const listeners = { logout: [], tokens: [] };

const readSession = () => {
  const session = {};
  SESSION_KEYS.forEach((key) => {
    const value = sessionStorage.getItem(key);
    if (value !== null) session[key] = value;
  });
  return session.accessToken && session.refreshToken && session.userData ? session : null;
};

const writeSession = (session) => {
  SESSION_KEYS.forEach((key) => {
    if (session[key] !== undefined && session[key] !== null) sessionStorage.setItem(key, session[key]);
  });
};

if (channel) {
  channel.onmessage = ({ data }) => {
    switch (data?.type) {
      case "ask": {
        // Another tab just opened: give it this tab's login, if signed in.
        const session = readSession();
        if (session) channel.postMessage({ type: "session", session });
        break;
      }
      case "tokens":
        // Another tab refreshed the tokens; the old refresh token may no longer be accepted.
        if (sessionStorage.getItem("accessToken") && data.tokens?.accessToken) {
          writeSession(data.tokens);
          listeners.tokens.forEach((callback) => callback(data.tokens));
        }
        break;
      case "logout":
        listeners.logout.forEach((callback) => callback());
        break;
      default:
        break;
    }
  };
}

// For a tab without a login: the login of another open tab, or null (none open / not signed in).
export const askOtherTabsForSession = () =>
  new Promise((resolve) => {
    if (!channel) {
      resolve(null);
      return;
    }
    const asking = new BroadcastChannel(CHANNEL_NAME);
    const done = (session) => {
      clearTimeout(timer);
      asking.close();
      resolve(session);
    };
    const timer = setTimeout(() => done(null), ASK_TIMEOUT_MS);
    asking.onmessage = ({ data }) => {
      if (data?.type === "session" && data.session?.accessToken) {
        writeSession(data.session);
        done(data.session);
      }
    };
    asking.postMessage({ type: "ask" });
  });

// After this tab refreshed the tokens.
export const shareTokens = (accessToken, refreshToken) => {
  if (channel && accessToken) channel.postMessage({ type: "tokens", tokens: { accessToken, refreshToken } });
};

// After this tab logged out (the other tabs log out too).
export const shareLogout = () => {
  if (channel) channel.postMessage({ type: "logout" });
};

const subscribe = (kind, callback) => {
  listeners[kind].push(callback);
  return () => {
    listeners[kind] = listeners[kind].filter((existing) => existing !== callback);
  };
};

// Called when another tab logged out. Returns a function that stops listening.
export const onOtherTabLogout = (callback) => subscribe("logout", callback);

// Called with {accessToken, refreshToken} when another tab refreshed them (already saved in this tab).
export const onOtherTabTokens = (callback) => subscribe("tokens", callback);
